import * as THREE from "three";
import { useThree, useFrame } from "@react-three/fiber";
import React, { useEffect, useRef, useCallback } from "react";
import { DragControls } from "three-stdlib";
import { colors } from "../../utils/colors";
import { useOrbit } from "../CustomHooks";

function CustomScaleHandles({ position, meshRef }) {
  const { disableOrbit, enableOrbit } = useOrbit();
  const xHandleRef = useRef();
  const yHandleRef = useRef();
  const zHandleRef = useRef();
  const allHandleRef = useRef();
  const resetButtonRef = useRef();
  const xLineRef = useRef();
  const yLineRef = useRef();
  const zLineRef = useRef();
  const allLineRef = useRef();

  const { gl, camera, setFrameloop } = useThree();
  const initialScale = useRef(new THREE.Vector3(1, 1, 1));
  const initialDistances = useRef({
    x: 0,
    y: 0,
    z: 0,
    all: 0,
  });
  const cubeCenter = useRef(new THREE.Vector3());

  const getHandleOffset = useCallback(() => {
    if (!meshRef?.current) return 6;

    const box = new THREE.Box3().setFromObject(meshRef.current);

    const size = box.getSize(new THREE.Vector3());
    const maxDimension = Math.max(size.x, size.y, size.z);
    return maxDimension * 0.6 + 3;
  }, [meshRef]);

  const createScaleHandleControls = useCallback(
    (handleRef, axis, defaultColor, isUniform = false) => {
      if (!handleRef.current || !meshRef.current) return null;

      const controls = new DragControls(
        [handleRef.current],
        camera,
        gl.domElement
      );

      controls.addEventListener("dragstart", (e) => {
        e.object.material.color.set(colors.warning());
        disableOrbit();
        setFrameloop("always");
        gl.domElement.style.cursor = "grabbing";
        cubeCenter.current.set(position[0], position[1], position[2]);
        initialScale.current.copy(meshRef.current.scale);

        const currentPos = e.object.position;
        const centerPos = cubeCenter.current;

        if (isUniform) {
          initialDistances.current.all = currentPos.distanceTo(centerPos);
        } else {
          const direction = currentPos.clone().sub(centerPos);
          initialDistances.current[axis] = Math.abs(direction[axis]);
        }
      });

      controls.addEventListener("drag", (e) => {
        const currentPos = e.object.position;
        const centerPos = cubeCenter.current;

        if (isUniform) {
          const distance = currentPos.distanceTo(centerPos);
          const scaleFactor = Math.pow(
            distance / initialDistances.current.all,
            3
          );
          const newScale = initialScale.current
            .clone()
            .multiplyScalar(scaleFactor);
          meshRef.current.scale.copy(newScale);
        } else {
          const direction = currentPos.clone().sub(centerPos);
          const distance = Math.abs(direction[axis]);
          const scaleFactor = Math.pow(
            distance / initialDistances.current[axis],
            3
          );

          const worldScaleDirection = new THREE.Vector3();
          worldScaleDirection[axis] = scaleFactor;

          const mesh = meshRef.current;
          const quaternion = mesh.quaternion.clone().invert();
          const localScaleDirection = worldScaleDirection
            .clone()
            .applyQuaternion(quaternion);

          const absX = Math.abs(localScaleDirection.x);
          const absY = Math.abs(localScaleDirection.y);
          const absZ = Math.abs(localScaleDirection.z);

          if (absX >= absY && absX >= absZ) {
            mesh.scale.x = initialScale.current.x * scaleFactor;
          } else if (absY >= absX && absY >= absZ) {
            mesh.scale.y = initialScale.current.y * scaleFactor;
          } else {
            mesh.scale.z = initialScale.current.z * scaleFactor;
          }
        }
      });

      controls.addEventListener("dragend", (e) => {
        e.object.material.color.set(defaultColor);
        enableOrbit();
        setFrameloop("demand");
        gl.domElement.style.cursor = "auto";
      });

      return controls;
    },
    [camera, gl, setFrameloop, meshRef, position, disableOrbit, enableOrbit]
  );

  useFrame(() => {
    if (!position) return;

    const handleOffset = getHandleOffset();
    const center = new THREE.Vector3(position[0], position[1], position[2]);
    cubeCenter.current.copy(center);

    if (xHandleRef.current) {
      xHandleRef.current.position.set(
        position[0] + handleOffset,
        position[1],
        position[2]
      );
    }
    if (yHandleRef.current) {
      yHandleRef.current.position.set(
        position[0],
        position[1] + handleOffset,
        position[2]
      );
    }
    if (zHandleRef.current) {
      zHandleRef.current.position.set(
        position[0],
        position[1],
        position[2] + handleOffset
      );
    }
    if (allHandleRef.current) {
      const offset = handleOffset * 0.8;
      allHandleRef.current.position.set(
        position[0] + offset,
        position[1] + offset,
        position[2] + offset
      );
    }

    if (resetButtonRef.current) {
      resetButtonRef.current.position.set(
        position[0],
        position[1] + handleOffset * 1.5,
        position[2]
      );
    }

    if (xLineRef.current && xHandleRef.current) {
      const points = [center, xHandleRef.current.position.clone()];
      xLineRef.current.geometry.setFromPoints(points);
    }

    if (yLineRef.current && yHandleRef.current) {
      const points = [center, yHandleRef.current.position.clone()];
      yLineRef.current.geometry.setFromPoints(points);
    }

    if (zLineRef.current && zHandleRef.current) {
      const points = [center, zHandleRef.current.position.clone()];
      zLineRef.current.geometry.setFromPoints(points);
    }

    if (allLineRef.current && allHandleRef.current) {
      const points = [center, allHandleRef.current.position.clone()];
      allLineRef.current.geometry.setFromPoints(points);
    }
  });

  useEffect(() => {
    const xControls = createScaleHandleControls(
      xHandleRef,
      "x",
      colors.handleX()
    );
    return () => xControls?.dispose();
  }, [createScaleHandleControls]);

  useEffect(() => {
    const yControls = createScaleHandleControls(
      yHandleRef,
      "y",
      colors.handleY()
    );
    return () => yControls?.dispose();
  }, [createScaleHandleControls]);

  useEffect(() => {
    const zControls = createScaleHandleControls(
      zHandleRef,
      "z",
      colors.handleZ()
    );
    return () => zControls?.dispose();
  }, [createScaleHandleControls]);

  useEffect(() => {
    const allControls = createScaleHandleControls(
      allHandleRef,
      "all",
      colors.handleAll(),
      true
    );
    return () => allControls?.dispose();
  }, [createScaleHandleControls]);

  return (
    <group>
      <mesh ref={xHandleRef}>
        <boxGeometry args={[0.8, 0.8, 0.8]} />
        <meshBasicMaterial color={colors.handleX()} />
      </mesh>

      <mesh ref={yHandleRef}>
        <boxGeometry args={[0.8, 0.8, 0.8]} />
        <meshBasicMaterial color={colors.handleY()} />
      </mesh>

      <mesh ref={zHandleRef}>
        <boxGeometry args={[0.8, 0.8, 0.8]} />
        <meshBasicMaterial color={colors.handleZ()} />
      </mesh>

      <mesh ref={allHandleRef}>
        <boxGeometry args={[0.8, 0.8, 0.8]} />
        <meshBasicMaterial color={colors.handleAll()} />
      </mesh>

      <line ref={xLineRef}>
        <bufferGeometry />
        <lineBasicMaterial color={colors.handleX()} linewidth={2} />
      </line>

      <line ref={yLineRef}>
        <bufferGeometry />
        <lineBasicMaterial color={colors.handleY()} linewidth={2} />
      </line>

      <line ref={zLineRef}>
        <bufferGeometry />
        <lineBasicMaterial color={colors.handleZ()} linewidth={2} />
      </line>

      <line ref={allLineRef}>
        <bufferGeometry />
        <lineBasicMaterial color={colors.handleAll()} linewidth={2} />
      </line>
    </group>
  );
}

export default CustomScaleHandles;
